import { useStripe, useElements, PaymentElement } from "@stripe/react-stripe-js";
import { useState } from "react";
import { toast } from "react-toastify";
import WebService from "../../../Services/WebService";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { storeAccessData } from "../../../config/action";

interface PropsData {
  paymentIntent: string;
  trnsId: string;
  onSuccess: () => void;
}

const CheckoutForm = ({ paymentIntent, trnsId, onSuccess }: PropsData) => {
  const stripe = useStripe();
  const elements = useElements();
  const activeLoginTypes = useSelector((state: any) => state?.items?.storeActiveLoginType);
  let account_type = activeLoginTypes?.account_type;
  let login_type = activeLoginTypes?.loginType;

  const [loading, setLoading] = useState(false);
  const [elementReady, setElementReady] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements || !elementReady || loading) return;

    setLoading(true);

    try {
      // 1. Submit and validate PaymentElement form fields before confirming
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setLoading(false);
        toast.error(submitError.message || "Please complete all payment fields.");
        return;
      }

      // 2. Confirm payment
      const { error, paymentIntent: updatedPaymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: "if_required",
      });

      if (error) {
        setLoading(false);
        toast.error(error.message || "Payment failed. Please try again.");
        return;
      }

      if (updatedPaymentIntent?.status === "succeeded") {
        handleDoPaymentApi();
      } else {
        setLoading(false);
        toast.info(`Payment status: ${updatedPaymentIntent?.status}`);
      }
    } catch (err: any) {
      setLoading(false);
      console.error("Payment confirmation error:", err);
      toast.error(err?.message || "An unexpected error occurred during payment.");
    }
  };

  const handleDoPaymentApi = () => {
    if (!paymentIntent || !trnsId) {
      setLoading(false);
      return;
    }
    return WebService.postAPI({
      action: "membership/payment",
      body: {
        payment_intent: paymentIntent,
        trx_id: trnsId,
      },
    })
      .then((res: any) => {
        if (res?.status) {
          setLoading(false);
          toast.success(res?.message || "Payment successful!");
          onSuccess();

          let profile_data = {
            market_access_count: res?.market_access_count,
            can_market_access_limited: res?.can_market_access_limited,
          };
          dispatch(storeAccessData(profile_data));
          navigate(`/${login_type}/${account_type}/manage-country/${trnsId}`);
        } else {
          toast.error(res?.message || "Payment verification failed.");
          setLoading(false);
        }
      })
      .catch((error: any) => {
        setLoading(false);
        toast.error(error?.response?.data?.message || "Payment verification failed.");
        return error;
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement
        onReady={() => setElementReady(true)}
        onLoadError={(e: any) => {
          console.error("Stripe PaymentElement load error", e);
          toast.error(
            e?.error?.message ||
              "Could not load the payment form. Check Stripe keys.",
          );
        }}
      />
      <div className="text-center mt-4">
        <button
          type="submit"
          className="booked-now btn btn-primary"
          disabled={!stripe || !elements || !elementReady || loading}
        >
          {loading
            ? "Processing…"
            : !elementReady
            ? "Loading payment form…"
            : "Pay"}
        </button>
      </div>
    </form>
  );
};

export default CheckoutForm;
